/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the hook to use randomised patient columns.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to create a context.
 */
import * as React from 'react';

/*
 * Used to type columns
 */
import { Column } from 'material-table';

/*
 * Used to type patients and institutions 
 */
import {
    IInstitution
} from '@ngt/opms';

import { Chip, makeStyles } from '@material-ui/core';

/*
 * Used to format datetimes
 */
import { DateTime } from 'luxon';



/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

import * as Dtos from '../api/dtos';

/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */
const useStyles = makeStyles(theme => ({
    chip: {
        backgroundColor: '#987ba7',
        color: theme.palette.common.white,
    },

}));

const useRandomisedPatientColumns = (institutions: IInstitution[] | null, showSite: boolean): Column<Dtos.Patient>[] => {
    const classes = useStyles();
    const columns = React.useMemo(() => {
        const c: (Column<Dtos.Patient> | boolean)[] = [
            {
                field: 'studyNumber',
                title: 'Study Number',
                width: 210
            },
            showSite && {
                field: 'institutionCode',
                title: 'Site Code',
                render: rowData => (
                    <Chip
                        label={rowData.institutionCode}
                        className={classes.chip}
                        size="small"
                    />
                ),
                width: 210
            },
            showSite && {
                field: 'institutionId',
                title: 'Institution',
                render: rowData => (
                    institutions?.find(i => i.id === rowData.institutionId)?.name
                )
            },
            {
                field: 'randomisationDate',
                title: 'Randomisation Date',
                render: rowData => (
                    rowData.randomisationDate ? DateTime.fromISO(rowData.randomisationDate).toFormat('dd/MM/yyyy') : null
                ),
                width: 210
            },
            {
                field: 'patientStateId',
                title: 'Planned use of Abemaciclib',
                width: 210,
                render: rowData => (rowData.patientStateId === Dtos.PatientStateType.RandomisedOnAbemaciclib || rowData.patientStateId === Dtos.PatientStateType.RandomisedOnAbemaciclibNz)  ? 'Yes' : (rowData.patientStateId === 3 ? 'No' : '')
            }
        ];

        return c.filter(column => !!column) as Column<Dtos.Patient>[];
    }, [showSite, institutions]);

    return columns;
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default useRandomisedPatientColumns;